/**
 * The workflow canvas's dot grid, as AmoregGTM's flow grid draws it: a
 * Canvas 2D pattern with five levels of detail that fade between 6 and 10px
 * spacing, repainted outside React whenever the view moves. The dots take
 * `--canvas-dot`.
 *
 * @module components/workflows/canvas/grid
 */
import { type RefObject, useEffect, useRef } from "react";

import { GRID_SIZE, type View } from "./geometry";

const DOT_RADIUS = 1.15;
const MIN_RADIUS = 0.75;
const LEVELS = 5;
const FADE_START = 6;
const FADE_END = 10;

const tiles = new Map<string, HTMLCanvasElement>();

function smoothstep(edge0: number, edge1: number, value: number) {
  const t = Math.min(1, Math.max(0, (value - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function dotTile(size: number, radius: number, color: string) {
  const key = `${size}:${radius.toFixed(2)}:${color}`;
  const cached = tiles.get(key);
  if (cached) return cached;
  if (tiles.size > 48) tiles.clear();
  const tile = document.createElement("canvas");
  tile.width = size;
  tile.height = size;
  const context = tile.getContext("2d");
  if (!context) return tile;
  context.fillStyle = color;
  context.beginPath();
  context.arc(size / 2, size / 2, radius, 0, Math.PI * 2);
  context.fill();
  tiles.set(key, tile);
  return tile;
}

function drawGrid(context: CanvasRenderingContext2D, view: View, width: number, ratio: number, color: string) {
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.clearRect(0, 0, context.canvas.width, context.canvas.height);
  const radius = Math.max(MIN_RADIUS, DOT_RADIUS * Math.sqrt(Math.min(view.zoom, 1.4))) * ratio;
  const originX = (width / 2 + view.x) * ratio;
  const originY = view.y * ratio;
  for (let level = 0; level < LEVELS; level++) {
    const spacing = GRID_SIZE * 2 ** level * view.zoom;
    const alpha = smoothstep(FADE_START, FADE_END, spacing);
    if (alpha <= 0.01 && level < LEVELS - 1) continue;
    const step = spacing * ratio;
    const size = Math.max(2, Math.round(step));
    const scale = step / size;
    const pattern = context.createPattern(dotTile(size, radius / scale, color), "repeat");
    if (!pattern) return;
    pattern.setTransform(new DOMMatrix([scale, 0, 0, scale, originX - step / 2, originY - step / 2]));
    context.globalAlpha = Math.max(alpha, level === LEVELS - 1 ? 1 : 0);
    context.fillStyle = pattern;
    context.fillRect(0, 0, context.canvas.width, context.canvas.height);
    if (alpha >= 0.999) break;
  }
  context.globalAlpha = 1;
}

/** A repaint the canvas calls on every view change. */
export type GridPainter = (view: View) => void;

/** `theme` repaints the dots when the theme (and so `--canvas-dot`) changes. */
export function CanvasGrid({
  viewRef,
  painterRef,
  theme,
}: {
  readonly viewRef: RefObject<View>;
  readonly painterRef: RefObject<GridPainter | null>;
  readonly theme: string | undefined;
}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    // jsdom has no 2D context: the grid is decoration, so it simply isn't drawn there.
    if (!canvas || typeof DOMMatrix === "undefined" || navigator.userAgent.includes("jsdom")) return;
    const context = canvas.getContext("2d");
    if (!context) return;
    let width = 0;
    let ratio = 1;
    const color = getComputedStyle(canvas).getPropertyValue("--canvas-dot").trim() || "transparent";
    const render = (view: View) => {
      if (!width) return;
      drawGrid(context, view, width, ratio, color);
    };
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      ratio = window.devicePixelRatio || 1;
      width = rect.width;
      canvas.width = Math.round(rect.width * ratio);
      canvas.height = Math.round(rect.height * ratio);
      render(viewRef.current);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();
    painterRef.current = render;
    return () => {
      observer.disconnect();
      painterRef.current = null;
    };
    // `theme` re-reads --canvas-dot when the theme changes.
    // oxlint-disable-next-line react/exhaustive-effect-dependencies
  }, [viewRef, painterRef, theme]);

  return <canvas ref={ref} aria-hidden className="pointer-events-none absolute inset-0 size-full" />;
}
